import React, { memo, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Dropdown from 'crehana-ui/Dropdown';
import { primaryMain } from 'crehana-ui/styles/colors';

import getStaticUrl from 'Utils/getStaticUrl';
import notEmpty from 'Jsx/Utils/notEmpty';

import { useMainFooterAssignCountryMutationMutation } from '../graphql/__generated__/assignCountry.gcg';
import { useMainFooterCountriesQueryQuery } from '../graphql/__generated__/currencyCountries.gcg';

const CircleAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LdsDualRing = styled.div`
  display: inline-block;
  width: 22px;
  height: 22px;
  &:after {
    content: ' ';
    display: block;
    width: 22px;
    height: 22px;
    margin: 1px;
    border-radius: 50%;
    border: 2px solid ${primaryMain};
    border-color: ${primaryMain} transparent ${primaryMain} transparent;
    animation: ${CircleAnimation} 1.2s linear infinite;
  }
`;

type CountryOption = {
  id: string;
  value: number | string;
  label: string | React.ReactNode;
  hiddeLabel?: string;
};

const CurrencyDropdownSelector = ({
  isDark,
  displayPosition = 'top',
}: {
  isDark?: boolean;
  displayPosition?: 'top' | 'bottom';
}) => {
  const [selectedCountry, setSelectedCountry] = useState({
    error: false,
    countryName: '',
    countryCode: 0,
    currencySymbolModal: '',
    currencyCodeModal: '',
  });
  const { data, loading } = useMainFooterCountriesQueryQuery();
  const [updateCountry] = useMainFooterAssignCountryMutationMutation();
  const [optionsCountry, setOptionsCountry] = useState<CountryOption[]>([]);

  const countries = useMemo(
    () =>
      data?.countriesV2
        ?.filter(
          country =>
            country && !['CR', 'EC', 'BO'].includes(country.code || ''),
        )
        .filter(notEmpty) || [],
    [data],
  );

  const getOptionsCountry = () => {
    const newOptions = countries?.map(c => ({
      id: c.code,
      value: c.name,
      hiddenLabel: c.name,
      label: (
        <div className="flex items-center text-16 font-nun">
          {c.code !== '*' ? (
            <img
              className="block w-24 h-16 mr-8"
              src={getStaticUrl(`img/flags/${c.code}-2.png`)}
              alt="country flag"
              area-label="flag"
            />
          ) : (
            <div className="px-16" />
          )}
          <div>
            <span
              className={`country font-bold ${
                isDark ? 'text-gray-dark' : 'text-base-main'
              }`}
            >
              {c.name}{' '}
            </span>
            <span
              className={`currency ${
                isDark ? 'text-gray-dark' : 'text-base-main'
              }`}
            >
              {c.currencySymbol} ( {c.currencyCode} )
            </span>
          </div>
        </div>
      ),
    }));

    setOptionsCountry(newOptions);
  };

  const getCurrencyCountry = () => {
    const currentCountry =
      countries.find(c => c.isCurrentCountry === true) ||
      countries.find(c => c.name === 'Otros');

    if (currentCountry) {
      setSelectedCountry({
        ...selectedCountry,
        countryName: currentCountry.name,
        countryCode: currentCountry.code,
        currencySymbolModal: currentCountry.currencySymbol,
        currencyCodeModal: currentCountry.currencyCode,
      });
    }
  };

  useEffect(() => {
    if (!loading) {
      getCurrencyCountry();
      getOptionsCountry();
    }
  }, [loading]);

  const changeCurrency = (countryOption?: Omit<CountryOption, 'id'>) => {
    if (countryOption) {
      const { value } = countryOption;
      const currentCountry = countries.find(c => c.name === value);

      if (currentCountry && currentCountry.code) {
        const countryCode = currentCountry.code;

        setSelectedCountry({
          ...selectedCountry,
          countryName: value,
          countryCode,
          currencyCodeModal: currentCountry.currencyCode,
          currencySymbolModal: currentCountry.currencySymbol,
        });

        updateCountry({
          variables: {
            countryCode,
            url: window.location.pathname,
          },
        })
          .then(({ data: res }) => {
            if (res?.assignCountry?.success && res.assignCountry.url) {
              window.location.href = res.assignCountry.url;
            } else {
              setSelectedCountry({
                ...selectedCountry,
                error: true,
              });
            }
          })
          .catch(err => {
            setSelectedCountry({
              ...selectedCountry,
              error: err,
            });
          });
      }
    }
  };

  const optionSelected = optionsCountry.find(
    optionCountry => optionCountry.value === selectedCountry.countryName,
  );

  if (loading) {
    return (
      <div className="flex justify-center" style={{ width: 258 }}>
        <LdsDualRing />
      </div>
    );
  }

  return (
    <div style={{ width: 260 }}>
      <Dropdown
        label=""
        onSelected={changeCurrency}
        options={optionsCountry}
        selected={optionSelected}
        displayPosition={displayPosition}
        dark={isDark}
        full
      />
    </div>
  );
};

export default memo(CurrencyDropdownSelector);
